<template>
   <div>
      <h5>Payment</h5>
      <p class="_margin-bottom-20">Our estimate for your monthly Direct Debit with {{ switchPlan.supplierName }}:</p>

      <div class="row _margin-bottom-20">
         <div class="col-md-6">
            <div class="display-data">
               <h4 class="_margin-top-0 _margin-bottom-0" v-if="switchPlan.gasCost">
                  {{ roundUp(divideBy(switchPlan.cost, 12) + divideBy(switchPlan.gasCost, 12)) | currency(2) }}
                  <small class="font-body">per month</small>
               </h4>
               <h4 class="_margin-top-0 _margin-bottom-0" v-if="!switchPlan.gasCost">
                  {{ roundUp(divideBy(switchPlan.cost, 12)) | currency(2) }} <small class="font-body">per month</small>
               </h4>
            </div>

         </div>
      </div>

      <p style="line-height: 1.3; font-size: 85%;">
         In order for {{ switchPlan.supplierName }} to setup your Direct Debit payment, please complete your
         bank/building society account details.
      </p>

      <div class="form-group _margin-bottom-20">
         <div class="col-sm-12">
            <form-label id="accountName">Card Holder Name</form-label>
            <validation-messages label="Card Holder Name" :$v="$v.form.accountName">
               <form-field id="accountName" v-model="payment.accountName" :error="$v.form.accountName.$error"
                  maxlength="100" />
            </validation-messages>
         </div>
      </div>

      <div class="form-group">
         <div class="col-xs-12 col-sm-6 _margin-bottom-20">
            <form-label id="sortCode1">Sort Code</form-label>
            <validation-messages label="Sort Code" :$v="$v.form.sortCode">
               <div class="sort-code">
                  <form-field ref="sortCode1" id="sortCode1" type="number" v-model="payment.sortCode1"
                     :error="$v.form.sortCode.$error" minlength="2" maxlength="2"
                     @change="onChange(payment.sortCode1, 'sortCode2')" />
                  <span class="sort-code-spacer">-</span>
                  <form-field ref="sortCode2" id="sortCode2" type="number" v-model="payment.sortCode2"
                     :error="$v.form.sortCode.$error" minlength="2" maxlength="2"
                     @change="onChange(payment.sortCode2, 'sortCode3')" />
                  <span class="sort-code-spacer">-</span>
                  <form-field ref="sortCode3" id="sortCode3" type="number" v-model="payment.sortCode3"
                     :error="$v.form.sortCode.$error" minlength="2" maxlength="2"
                     @change="onChange(payment.sortCode3, 'accountNumber')" />
               </div>
            </validation-messages>
         </div>

         <div class="col-xs-12 col-sm-6 _margin-bottom-20">
            <form-label id="accountNumber">Account Number</form-label>
            <validation-messages label="Account Number" :$v="$v.form.accountNumber">
               <form-field ref="accountNumber" id="accountNumber" type="number" v-model="payment.accountNumber"
                  :error="$v.form.accountNumber.$error" minlength="6" maxlength="8" />
            </validation-messages>
         </div>
      </div>

      <div class="form-group _margin-bottom-20">
         <div class="col-sm-5 col-md-12">
            <form-label id="preferredDirectDebitDay">I want my Direct Debit to go out on the:</form-label>

            <validation-messages label="Preferred Direct Debit Day" :$v="$v.form.preferredDirectDebitDay">
               <select-list id="preferredDirectDebitDay" v-model="payment.preferredDirectDebitDay" value-key="id"
                  :list="directDebitDaysList" :error="$v.form.preferredDirectDebitDay.$error" />
            </validation-messages>
         </div>
      </div>

      <div class="form-group _margin-bottom-50">
         <div class="col-sm-12">
            <validation-messages label="Confirmation" :$v="$v.form.isAuthorisedToSetupDirectDebits">
               <checkbox id="isAuthorisedToSetupDirectDebits" v-model="payment.isAuthorisedToSetupDirectDebits"
                  :error="$v.form.isAuthorisedToSetupDirectDebits.$error"
                  @change="payment.isAuthorisedToSetupDirectDebits = payment.isAuthorisedToSetupDirectDebits ? true : null">
                  I confirm that I am the account holder and am the only person required to authorise Direct
                  Debits from my bank account.
               </checkbox>
            </validation-messages>
         </div>
      </div>

      <div class="sticky-footer">
         <div class="col-xs-5">
            <uiv-btn type="link" block @click="goToQuestion('terms')">Back</uiv-btn>
         </div>
         <div class="col-xs-7">
            <uiv-btn type="primary" block @click="onNext">Next</uiv-btn>
         </div>
      </div>
   </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { requiredIf, maxLength, minLength, numeric } from 'vuelidate/lib/validators';

export default {
   name: 'PaymentDetails',
   data() {
      return {
         error: {},
         today: new Date(),
         requesting: false
      };
   },
   mounted() {
      this.form = this.payment;
   },
   computed: {
      ...mapState({
         payment: ({ personal_details_payment }) => personal_details_payment,
         directDebitDaysList: ({ lookups }) => lookups.directDebitDays,
         switchPlan: ({ personal_details_switch_plan }) => personal_details_switch_plan
      })
   },
   validations() {
      return {
         form: {
            accountName: {
               requiredIf: requiredIf(() => {
                  const { payMethod } = this.switchPlan;
                  return payMethod == 'MonthlyDirectDebit' || payMethod == 'QuaterlyDirectDebit';
               })
            },
            sortCode: {
               requiredIf: requiredIf(() => {
                  const { payMethod } = this.switchPlan;
                  return payMethod == 'MonthlyDirectDebit' || payMethod == 'QuaterlyDirectDebit';
               }),
               maxLength: maxLength(6),
               minLength: minLength(6)
            },
            sortCode1: {
               requiredIf: requiredIf(() => {
                  const { payMethod } = this.switchPlan;
                  return payMethod == 'MonthlyDirectDebit' || payMethod == 'QuaterlyDirectDebit';
               }),
               numeric,
               maxLength: maxLength(2)
            },
            sortCode2: {
               requiredIf: requiredIf(() => {
                  const { payMethod } = this.switchPlan;
                  return payMethod == 'MonthlyDirectDebit' || payMethod == 'QuaterlyDirectDebit';
               }),
               numeric,
               maxLength: maxLength(2)
            },
            sortCode3: {
               requiredIf: requiredIf(() => {
                  const { payMethod } = this.switchPlan;
                  return payMethod == 'MonthlyDirectDebit' || payMethod == 'QuaterlyDirectDebit';
               }),
               numeric,
               maxLength: maxLength(2)
            },
            accountNumber: {
               requiredIf: requiredIf(() => {
                  const { payMethod } = this.switchPlan;
                  return payMethod == 'MonthlyDirectDebit' || payMethod == 'QuaterlyDirectDebit';
               }),
               numeric,
               maxLength: maxLength(8),
               minLength: minLength(6)
            },
            preferredDirectDebitDay: {
               requiredIf: requiredIf(() => {
                  const { payMethod } = this.switchPlan;
                  return payMethod == 'MonthlyDirectDebit' || payMethod == 'QuaterlyDirectDebit';
               })
            },
            isAuthorisedToSetupDirectDebits: {
               requiredIf: requiredIf(() => {
                  const { payMethod } = this.switchPlan;
                  return payMethod == 'MonthlyDirectDebit' || payMethod == 'QuaterlyDirectDebit';
               })
            }
         }
      }
   },
   methods: {
      ...mapActions({
         goToQuestion: 'personal_details_questions/goToQuestion'
      }),
      onChange(value, next) {
         const { sortCode1, sortCode2, sortCode3 } = this.payment;

         if (value) {
            this.payment.sortCode = `${sortCode1 || 0}${sortCode2 || 0}${sortCode3 || 0}`;
            if (value.length == 2) {
               this.$refs[next].$el.focus();
            }
         }
      },
      onNext() {
         this.$v.form.$touch();
         if (this.$v.form.$invalid) return;
         this.$scrollTo('.fullscreen-card');
         this.goToQuestion('confirm');
      },
      divideBy(number, by) {
         if (!number) {
            return number;
         }

         return number / by;
      },
      roundUp(number) {
         return Math.ceil(number);
      }
   }
};
</script>

<style lang="scss" scoped>
.sort-code {
   .form-control {
      display: inline-block;
      max-width: 50px;
   }

   .sort-code-spacer {
      margin: 0 3px;
   }
}

.font-body {
   font-family: $font-family-body !important;
   font-weight: 200;
}
</style>
